import { useState, useEffect, useCallback } from "react";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { db } from "../../../../services/firebase";
import { useTenant } from "../../../../hooks/useTenant";
import { COLLECTIONS } from "../../../../services/collections";
import { enrollmentFromFirestore, enrollmentToFirestore } from "../../../../models/enrollment/enrollmentConverter";
import { toISODate } from "../../../../utils/date";

export const useTrialsData = (periodStart, periodEnd) => {
    const { idTenant } = useTenant();
    const [trials, setTrials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchTrials = useCallback(async () => {
        if (!idTenant) return;

        setLoading(true);
        setError(null);

        try {
            const startStr = typeof periodStart === "string" ? periodStart : toISODate(periodStart);
            const endStr = typeof periodEnd === "string" ? periodEnd : toISODate(periodEnd);

            const converter = { toFirestore: enrollmentToFirestore, fromFirestore: enrollmentFromFirestore };
            const trialsRef = collection(db, "tenants", idTenant, COLLECTIONS.ENROLLMENTS).withConverter(converter);
            const q = query(
                trialsRef,
                where("type", "==", "trial"),
                where("sessionDate", ">=", startStr),
                where("sessionDate", "<=", endStr),
                orderBy("sessionDate", "desc")
            );

            const snapshot = await getDocs(q);
            const data = snapshot.docs.map((doc) => doc.data());

            // Ordenação: data DESC, hora DESC.
            data.sort((a, b) => {
                const dateA = a.sessionDate || "";
                const dateB = b.sessionDate || "";
                if (dateA !== dateB) return dateB.localeCompare(dateA);

                const timeA = a.sessionTime || "";
                const timeB = b.sessionTime || "";
                return timeB.localeCompare(timeA);
            });

            setTrials(data);
        } catch (err) {
            console.error("Erro ao carregar experimentais:", err);
            // Ignore o erro de índice caso ainda não tenha sido criado pelo Firestore:
            if (err.message && err.message.includes("requires an index")) {
                setError("O sistema está configurando a base de dados. Recarregue a página em alguns instantes.");
            } else {
                setError("Falha ao carregar aulas experimentais.");
            }
        } finally {
            setLoading(false);
        }
    }, [idTenant, periodStart, periodEnd]);

    useEffect(() => {
        fetchTrials();
    }, [fetchTrials]);

    return { trials, loading, error, refetch: fetchTrials };
};
